import {
  Component, OnInit, OnDestroy, DestroyRef, inject, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef, Input, HostBinding
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { trigger, transition, style, animate } from '@angular/animations';

import { AIChatPanelService } from './ai-chat-panel.service';
import { OpenAIChatService, StreamChunkEvent, DocumentFileDetails } from './openai-chat.service';
import { UserService } from '../services/user.service';
import { CompaniesService } from '../services/companies.service';
import { UserVM } from '../models/user/user-vm.model';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// ============================================================
// Local Interfaces
// ============================================================

export interface AIChatConfig {
  systemMessage?: string;
  inputPlaceholder?: string;
  title?: string;
  icon?: string;
  companyId?: number;
  entityContext?: string;
  enableDocuments?: boolean;
  enableStreaming?: boolean;
  temperature?: number;
  maxTokens?: number;
  suggestedPrompts?: string[];
  showHeader?: boolean;
  containerClass?: string;
}

export interface AIChatDisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  renderedContent?: SafeHtml;
  timestamp: Date;
  isStreaming?: boolean;
  attachments?: { name: string; fileId?: number; size?: number }[];
  isError?: boolean;
}

@Component({
  selector: 'app-ai-chat-note',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
  ],
  templateUrl: './ai-chat-note.component.html',
  styleUrls: ['./ai-chat-note.component.scss'],
  animations: [
    trigger('chatPopup', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.85)' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 0, transform: 'scale(0.85)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatNoteComponent implements OnInit, OnDestroy {

  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() config: AIChatConfig = {};

  @HostBinding('class.chat-opened') get chatOpened(): boolean { return this.isOpen; }

  readonly panelService = inject(AIChatPanelService);
  private readonly chatService = inject(OpenAIChatService);
  private readonly userService = inject(UserService);
  private readonly companiesService = inject(CompaniesService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);

  // State
  isOpen = false;
  messages: AIChatDisplayMessage[] = [];
  messageControl = new FormControl('');
  isLoading = false;
  isInitializing = false;
  isStreaming = false;
  sessionActive = false;
  currentUser: UserVM | null = null;
  pendingFiles: File[] = [];
  uploadedDocs: DocumentFileDetails[] = [];

  // Computed defaults
  get title(): string { return this.config.title || 'AI Assistant'; }
  get icon(): string { return this.config.icon || 'smart_toy'; }
  get placeholder(): string { return this.config.inputPlaceholder || 'Ask me anything...'; }
  get showHeader(): boolean { return this.config.showHeader !== false; }
  get enableDocs(): boolean {
    return !!(this.config.companyId || this.companiesService.managedCompanyId);
  }
  private get activeCompanyId(): number {
    return this.config.companyId || this.companiesService.managedCompanyId;
  }
  get enableStreaming(): boolean { return this.config.enableStreaming !== false; }
  get suggestedPrompts(): string[] {
    return this.config.suggestedPrompts || [
      'Summarize this conversation',
      'What are the key action items?',
      'Draft a follow-up email',
      'Analyze the attached documents',
    ];
  }

  private destroyed$ = new Subject<void>();

  // ============================================================
  // Lifecycle
  // ============================================================

  ngOnInit(): void {
    this.loadCurrentUser();

    // Subscribe to panel open/close state
    this.panelService.isOpen$.pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(isOpen => {
      const wasOpen = this.isOpen;
      this.isOpen = isOpen;

      // Initialize session when first opened
      if (isOpen && !wasOpen && !this.sessionActive && !this.isInitializing) {
        this.initSession();
      }

      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.sessionActive) {
      this.chatService.deleteSession().subscribe();
    }
  }

  // ============================================================
  // Initialization
  // ============================================================

  private loadCurrentUser(): void {
    this.userService.getCurrentUser(false).pipe(
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroy)
    ).subscribe(user => {
      this.currentUser = user;
      this.cdr.markForCheck();
    });
  }

  private initSession(): void {
    this.isInitializing = true;
    this.cdr.markForCheck();

    const systemMsg = this.buildSystemMessage();

    this.chatService.createSession(systemMsg).pipe(
      catchError(err => {
        this.snackBar.open('Failed to initialize AI chat session', 'Close', { duration: 4000 });
        this.isInitializing = false;
        this.cdr.markForCheck();
        return of(null);
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(res => {
      if (res) {
        this.sessionActive = true;
      }
      this.isInitializing = false;
      this.cdr.markForCheck();
    });
  }

  private buildSystemMessage(): string {
    let msg = this.config.systemMessage || 'You are a helpful AI assistant integrated into a business management application. You help users analyze documents, summarize conversations, draft communications, and provide insights. Be concise, professional, and actionable in your responses. Format responses using markdown when appropriate.';

    if (this.config.entityContext) {
      msg += `\n\nContext: ${this.config.entityContext}`;
    }

    return msg;
  }

  // ============================================================
  // Chat Actions
  // ============================================================

  sendMessage(): void {
    const content = this.messageControl.value?.trim();
    if (!content && !this.pendingFiles.length) return;
    if (this.isStreaming || this.isLoading) return;

    if (this.pendingFiles.length > 0 && this.enableDocs) {
      this.uploadAndSend(content || 'Please analyze this document.');
      return;
    }

    if (!content) return;

    const userMsg = this.createDisplayMessage('user', content);
    this.messages.push(userMsg);
    this.messageControl.setValue('');
    this.cdr.markForCheck();
    this.scrollToBottom();

    if (this.enableStreaming) {
      this.sendStreaming(content);
    } else {
      this.sendNonStreaming(content);
    }
  }

  private sendNonStreaming(message: string): void {
    this.isLoading = true;
    const assistantMsg = this.createDisplayMessage('assistant', '', true);
    this.messages.push(assistantMsg);
    this.cdr.markForCheck();

    this.chatService.sendSessionMessage(message).pipe(
      catchError(err => {
        this.handleChatError(err, assistantMsg);
        return of(null);
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(response => {
      if (response) {
        assistantMsg.content = response;
        assistantMsg.renderedContent = this.renderMarkdown(response);
        assistantMsg.isStreaming = false;
      }
      this.isLoading = false;
      this.cdr.markForCheck();
      this.scrollToBottom();
    });
  }

  private sendStreaming(message: string): void {
    this.isStreaming = true;
    const assistantMsg = this.createDisplayMessage('assistant', '', true);
    this.messages.push(assistantMsg);
    this.cdr.markForCheck();

    const stream$ = this.chatService.streamSessionMessage(message);

    stream$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (event: StreamChunkEvent) => {
        switch (event.type) {
          case 'chunk':
            assistantMsg.content += event.content || '';
            assistantMsg.renderedContent = this.renderMarkdown(assistantMsg.content);
            this.cdr.markForCheck();
            this.scrollToBottom();
            break;
          case 'done':
            assistantMsg.isStreaming = false;
            this.isStreaming = false;
            this.cdr.markForCheck();
            this.scrollToBottom();
            break;
          case 'error':
            this.handleChatError(event.error, assistantMsg);
            break;
        }
      },
      error: (err) => {
        this.handleChatError(err, assistantMsg);
      }
    });
  }

  private handleChatError(err: any, assistantMsg: AIChatDisplayMessage): void {
    const isExpired = err?.status === 404 || (typeof err === 'string' && err.includes('404'));

    if (isExpired) {
      assistantMsg.content = 'Session expired. Reconnecting...';
      assistantMsg.isError = true;
      assistantMsg.isStreaming = false;
      this.isStreaming = false;
      this.isLoading = false;
      this.cdr.markForCheck();
      this.reconnectSession();
    } else {
      assistantMsg.content = 'Sorry, something went wrong. Please try again.';
      assistantMsg.isError = true;
      assistantMsg.isStreaming = false;
      this.isStreaming = false;
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private reconnectSession(): void {
    this.sessionActive = false;
    this.chatService.deleteSession().pipe(
      catchError(() => of(null))
    ).subscribe(() => {
      this.initSession();
      this.snackBar.open('Session reconnected', 'Close', { duration: 2000 });
    });
  }

  // ============================================================
  // Document Upload
  // ============================================================

  triggerFileUpload(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      if (file.size > 50 * 1024 * 1024) {
        this.snackBar.open(`File "${file.name}" exceeds 50MB limit`, 'Close', { duration: 3000 });
        continue;
      }
      this.pendingFiles.push(file);
    }
    input.value = '';
    this.cdr.markForCheck();
  }

  removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    this.cdr.markForCheck();
  }

  private uploadAndSend(message: string): void {
    if (!this.activeCompanyId) return;

    const companyId = this.activeCompanyId;
    const file = this.pendingFiles[0];
    this.pendingFiles = [];

    const userMsg = this.createDisplayMessage('user', message, false, [{ name: file.name, size: file.size }]);
    this.messages.push(userMsg);
    this.messageControl.setValue('');
    this.cdr.markForCheck();

    this.isLoading = true;
    const assistantMsg = this.createDisplayMessage('assistant', 'Analyzing document...', true);
    this.messages.push(assistantMsg);
    this.cdr.markForCheck();
    this.scrollToBottom();

    const sessionId = this.chatService.currentSessionId || undefined;

    this.chatService.uploadDocument(companyId, file, {
      userMessage: message,
      sessionId,
    }).pipe(
      catchError(err => {
        assistantMsg.content = 'Failed to upload document. Please try again.';
        assistantMsg.isError = true;
        assistantMsg.isStreaming = false;
        this.isLoading = false;
        this.cdr.markForCheck();
        return of(null);
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(result => {
      if (result) {
        const uploadResult = (result as any).analysisResult || result;
        const analysis = uploadResult.extractedData;
        const docType = uploadResult.detectedDocumentType || 'Unknown';

        if (analysis) {
          try {
            const parsed = typeof analysis === 'string' ? JSON.parse(analysis) : analysis;
            assistantMsg.content = `Document analyzed successfully.\n\n**Detected Type:** ${docType}\n\n\`\`\`json\n${JSON.stringify(parsed, null, 2)}\n\`\`\`\n\nThe document is now in the session context. You can ask follow-up questions about it.`;
          } catch {
            assistantMsg.content = `Document analyzed successfully.\n\n**Detected Type:** ${docType}\n\n${analysis}\n\nYou can ask follow-up questions about the document.`;
          }
          assistantMsg.renderedContent = this.renderMarkdown(assistantMsg.content);
        } else {
          assistantMsg.content = 'Document uploaded successfully. You can now ask questions about it.';
        }
        assistantMsg.isStreaming = false;
        this.isLoading = false;
        this.cdr.markForCheck();
        this.scrollToBottom();
      }
    });
  }

  // ============================================================
  // Suggested Prompts
  // ============================================================

  useSuggestedPrompt(prompt: string): void {
    this.messageControl.setValue(prompt);
    this.sendMessage();
  }

  // ============================================================
  // Session Controls
  // ============================================================

  newSession(): void {
    if (this.isStreaming) return;
    this.messages = [];
    this.pendingFiles = [];
    this.sessionActive = false;
    this.chatService.deleteSession().pipe(
      catchError(() => of(null))
    ).subscribe(() => {
      this.initSession();
      this.cdr.markForCheck();
    });
  }

  exportChat(): void {
    const content = this.messages
      .filter(m => m.role !== 'system')
      .map(m => `[${m.role.toUpperCase()}] ${new Date(m.timestamp).toLocaleString()}\n${m.content}`)
      .join('\n\n---\n\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-chat-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  copyMessage(message: AIChatDisplayMessage): void {
    navigator.clipboard.writeText(message.content).then(() => {
      this.snackBar.open('Copied to clipboard', 'Close', { duration: 2000 });
    });
  }

  retryLastMessage(): void {
    const lastUserMsg = [...this.messages].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return;

    const lastIdx = this.messages.length - 1;
    if (this.messages[lastIdx]?.role === 'assistant') {
      this.messages.pop();
    }

    this.cdr.markForCheck();

    if (this.enableStreaming) {
      this.sendStreaming(lastUserMsg.content);
    } else {
      this.sendNonStreaming(lastUserMsg.content);
    }
  }

  // ============================================================
  // Keyboard Shortcuts
  // ============================================================

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // ============================================================
  // Helpers
  // ============================================================

  private createDisplayMessage(
    role: 'user' | 'assistant' | 'system',
    content: string,
    isStreaming = false,
    attachments?: { name: string; fileId?: number; size?: number }[]
  ): AIChatDisplayMessage {
    return {
      id: this.generateId(),
      role,
      content,
      renderedContent: content ? this.renderMarkdown(content) : undefined,
      timestamp: new Date(),
      isStreaming,
      attachments,
      isError: false,
    };
  }

  private renderMarkdown(content: string): SafeHtml {
    try {
      let html = this.escapeHtml(content);

      // Code blocks
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
      // Inline code
      html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
      // Headers
      html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
      html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
      html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>');
      // Bold
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
      // Blockquotes
      html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');
      // Horizontal rules
      html = html.replace(/^---$/gm, '<hr/>');
      // Unordered lists
      html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
      // Ordered lists
      html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
      // Line breaks (but not inside pre blocks)
      html = html.replace(/\n/g, '<br/>');
      // Clean up br inside pre
      html = html.replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g, (match, code) => {
        return '<pre><code>' + code.replace(/<br\/>/g, '\n') + '</code></pre>';
      });

      return this.sanitizer.bypassSecurityTrustHtml(html);
    } catch {
      return this.sanitizer.bypassSecurityTrustHtml(content);
    }
  }

  private escapeHtml(text: string): string {
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };
    return text.replace(/[&<>]/g, ch => map[ch]);
  }

  private generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      try {
        if (this.chatContainer) {
          this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
        }
      } catch {}
    }, 50);
  }

  getUserInitials(): string {
    if (!this.currentUser) return 'U';
    return `${(this.currentUser.firstName || '').charAt(0)}${(this.currentUser.lastName || '').charAt(0)}`.toUpperCase() || 'U';
  }

  getUserName(): string {
    if (!this.currentUser) return 'You';
    return `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim() || 'You';
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatFileSize(bytes: number | undefined): string {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'insert_drive_file';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc': case 'docx': return 'description';
      case 'xls': case 'xlsx': return 'table_chart';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'image';
      default: return 'insert_drive_file';
    }
  }

  trackByMessageId(index: number, msg: AIChatDisplayMessage): string {
    return msg.id;
  }
}
